import Cookies from 'js-cookie';

/**
 * Comunicación web <-> app nativa (WebView Android / WKWebView iOS).
 * Contrato completo en doc/ios-app/README.md.
 *
 * - Android: `window.JSBridge` (addJavascriptInterface), métodos síncronos.
 * - iOS: `window.webkit.messageHandlers.JSBridge` (WKScriptMessageHandler),
 *   se envía `{ method, ...args }` vía postMessage (sin valor de retorno).
 */

export type NativeMessage = 'logout' | 'login-shown' | 'logout-403';

/** Estado de App Tracking Transparency que informa la app iOS. */
export type IOSTrackingStatus =
  | 'authorized'
  | 'denied'
  | 'restricted'
  | 'notDetermined';

type IOSBridgeMessage =
  | { method: 'notifyMessage'; message: NativeMessage }
  | {
      method: 'shareImage';
      auth: string;
      base64Data: string;
      filename: string;
    };

declare global {
  interface Window {
    /**
     * Bridge entre la app nativa Android y la web.
     * https://medium.com/@sreeharikv112/communication-from-webview-to-native-ios-android-app-6d842cefe02d
     */
    JSBridge?: {
      notifyMessage: (message: string) => void;
      getInformation: (auth: string, key: string) => string | null;
      /**
       * Comparte un archivo (base64, sin el prefijo data:...;base64,) vía el
       * share sheet nativo de Android. Método opcional: solo existe en
       * versiones de la app que ya lo implementaron.
       */
      shareImage?: (auth: string, base64Data: string, filename: string) => void;
    };
    /** Bridge de la app iOS (WKWebView). */
    webkit?: {
      messageHandlers?: {
        JSBridge?: { postMessage: (message: IOSBridgeMessage) => void };
      };
    };
  }
}

/*
 * La webview de android setea esta cookie.
 * Simular así:
 * document.cookie = 'isAndroid=true';
 */
export function isAndroid() {
  const isAndroid = Cookies.get('isAndroid') === 'true';
  return isAndroid;
}

/*
 * La app iOS setea esta cookie en el WKHTTPCookieStore antes de cargar la web.
 * Simular así:
 * document.cookie = 'isIOS=true';
 */
export function isIOSApp() {
  return Cookies.get('isIOS') === 'true';
}

/** Corre dentro de alguna de las apps nativas (Android o iOS). */
export function isNativeApp() {
  return isAndroid() || isIOSApp();
}

const iosBridge = () => window.webkit?.messageHandlers?.JSBridge;

/**
 * Token random por sesión de WebView que la app comparte vía cookie.
 * La app lo valida en los métodos sensibles del bridge (shareImage, getInformation).
 */
export function getNativeWvAuth(): string | undefined {
  if (isIOSApp()) return Cookies.get('ios_wvAuth');
  return Cookies.get('android_wvAuth');
}

/** Cookie de redirección (tkRed) que la app setea al abrirse desde un link. */
export const NATIVE_TKRED_COOKIES = ['android_tkRed', 'ios_tkRed'];

export function notifyNative(message: NativeMessage) {
  if (isIOSApp()) {
    iosBridge()?.postMessage({ method: 'notifyMessage', message });
    return;
  }
  window.JSBridge?.notifyMessage(message);
}

export function canNativeShareImage() {
  if (!getNativeWvAuth()) return false;
  if (isIOSApp()) return Boolean(iosBridge());
  return Boolean(window.JSBridge?.shareImage);
}

/** Abre el share sheet nativo con la imagen. Lanza error si no hay bridge. */
export function nativeShareImage(base64Data: string, filename: string) {
  const auth = getNativeWvAuth();
  if (!auth) throw new Error('nativeShareImage: sin token wvAuth');

  if (isIOSApp()) {
    const bridge = iosBridge();
    if (!bridge) throw new Error('nativeShareImage: sin bridge iOS');
    bridge.postMessage({ method: 'shareImage', auth, base64Data, filename });
    return;
  }

  if (!window.JSBridge?.shareImage) {
    throw new Error('nativeShareImage: sin bridge Android');
  }
  window.JSBridge.shareImage(auth, base64Data, filename);
}

/**
 * App Tracking Transparency (Apple 5.1.2).
 * La app iOS pide el permiso ATT antes de cargar la web y deja el resultado en
 * la cookie `ios_att`. Fuera de la app iOS no se restringe nada.
 * Si la cookie no llegó, se asume que no hay permiso.
 */
export function isTrackingAllowed() {
  if (!isIOSApp()) return true;
  const status = Cookies.get('ios_att') as IOSTrackingStatus | undefined;
  return status === 'authorized';
}
